import type { FactoryCriticalZoneItem, FactoryTtqProduct } from '../../services/factoryService';
import { factoryQuestionStemPreview } from './factoryQuestionStemPreview';

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** warehouseTtqaId stamped on critical candidate when resolving products. */
export function criticalWarehouseTtqaId(item: FactoryCriticalZoneItem): string {
  const candidate = asRecord(item.candidate) || {};
  return String(candidate.warehouseTtqaId || '').trim();
}

export function criticalParticleIds(item: FactoryCriticalZoneItem): string[] {
  const candidate = asRecord(item.candidate) || {};
  const fromField = Array.isArray(candidate.particleIds)
    ? candidate.particleIds.map((id) => String(id || '').trim()).filter(Boolean)
    : [];
  if (fromField.length) return fromField;
  const members = Array.isArray(candidate.members) ? candidate.members : [];
  const ids: string[] = [];
  for (const raw of members) {
    const m = asRecord(raw);
    const pid = String(m?.particleId || '').trim();
    if (pid) ids.push(pid);
  }
  return ids;
}

export const TTQ_CRITICAL_REASON_LABEL: Record<string, string> = {
  flash_reject: 'Flash 否决（与成品智能体脉络不相关）',
  over_cap: '触顶（成品智能体成员已满）',
};

export const FACTORY_WAREHOUSE_STATUS_LABEL: Record<string, string> = {
  none: '未入库',
  in_qc: '抽检中',
  ready: '可下线',
  offline: '已下线待释放',
  scrapped: '已报废',
};

/** Decode critical-zone candidate into 颗粒 / 推荐组 / 目标成品智能体 for review UI. */
export function criticalIngressView(
  item: FactoryCriticalZoneItem,
  productsById: Map<string, FactoryTtqProduct>,
) {
  const candidate = asRecord(item.candidate) || {};
  const groupTitle = String(candidate.title || '').trim() || '未命名推荐组';
  const members = Array.isArray(candidate.members) ? candidate.members : [];
  const stemsRaw = Array.isArray(candidate.stems) ? candidate.stems : [];
  const particleIds = Array.isArray(candidate.particleIds)
    ? candidate.particleIds.map((id) => String(id || '').trim()).filter(Boolean)
    : [];

  const particleLines: string[] = [];
  for (const raw of members) {
    const m = asRecord(raw);
    if (!m) continue;
    const snap = asRecord(m.snapshot) || {};
    const subs = Array.isArray(snap.subQuestions)
      ? snap.subQuestions.map((s) => String(s || '').trim()).filter(Boolean)
      : Array.isArray(m.subQuestions)
        ? m.subQuestions.map((s) => String(s || '').trim()).filter(Boolean)
        : [];
    const stem = factoryQuestionStemPreview(
      String(snap.particleQuestions || snap.particleTitle || m.particleId || ''),
      { subQuestions: subs },
    );
    if (stem) particleLines.push(stem);
  }
  if (!particleLines.length) {
    for (const s of stemsRaw) {
      const stem = factoryQuestionStemPreview(String(s || ''));
      if (stem) particleLines.push(stem);
    }
  }
  if (!particleLines.length && particleIds.length) {
    for (const id of particleIds.slice(0, 3)) {
      particleLines.push(`颗粒 ${id.slice(0, 8)}…`);
    }
  }

  const product = item.productId ? productsById.get(item.productId) : undefined;
  const productTitle = product?.title?.trim()
    || (item.productId ? `成品智能体 ${item.productId.slice(0, 8)}…` : '未绑定成品智能体');
  const productCap = product
    ? `${product.memberCount}/${product.memberCap}`
    : null;

  const particleLevel = item.reason === 'over_cap' && particleLines.length <= 1;
  const reasonDetail = item.reason === 'over_cap'
    ? (particleLevel
      ? '该颗粒因成品智能体成员上限溢出，未能自动入组'
      : '推荐组内部分颗粒因成品智能体成员上限溢出')
    : 'Flash 判定与目标成品智能体脉络不相关，整组候选进临界区';

  return {
    groupTitle,
    particleLines,
    particleLevel,
    productTitle,
    productCap,
    reasonDetail,
    subjectLabel: item.subjectCode === 'yuwen' ? '语文' : '数学',
  };
}
